import { MessageSquareIcon, PenLineIcon } from "lucide-react";
import  { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUserId, setToken, setRefreshToken, setFullName, setProfileImage, setGoogleLocationApiKey } from "../../store/slices/userSlice";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { toast } from "sonner";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../components/ui/input-otp";
import MainLogo from "../../components/logo";
import { authApi } from "../../lib/api";
import { Loader } from "../../components/ui/loader";
import SuccessPopup from "../../components/ui/success-popup";
import { useTranslation } from "react-i18next";
import webbg from "../../assets/webbg.png";
import mobilebg from "../../assets/mobilebg.png";

export const Otp = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [otp, setOtp] = useState<string>("");
  const [timer, setTimer] = useState<number>(60);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract country from the path
  const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
  const country = countryMatch ? countryMatch[1] : "in";

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Effect to handle success popup timing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (showSuccessPopup) {
      timeoutId = setTimeout(() => {
        setShowSuccessPopup(false);
      }, 2000); // Show popup for 2 seconds
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showSuccessPopup]);

  // Check if we have the required state
  useEffect(() => {
    if (!location.state?.mobileNumber) {
      toast.error(t('invalidAccess'));
      navigate(`/${country}/login`);
    }
  }, [location.state, navigate, t, country]);

  // If no mobile number in state, return a loading state
  if (!location.state?.mobileNumber) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader isLoading={true} message={t('redirecting')} />
      </div>
    );
  }

  // Only destructure after we've confirmed the state exists
  const { mobileNumber, countryCode } = location.state;

  const handleGoBack = () => {
    navigate(`/${country}/login`, { 
      replace: true,
      state: { 
        mobileNumber: mobileNumber,
        countryCode: countryCode 
      }
    });
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    try {
      const cleanCountryCode = countryCode.replace("+", "");
      const response = await authApi.sendOtp(mobileNumber, cleanCountryCode);
      toast.success(response.message);
      setTimer(60); // Reset timer to 60 seconds
      setOtp(""); // Clear the OTP input field
      setError(""); // Clear any error messages
      // Force a re-render of the OTP input fields
      const otpInputs = document.querySelectorAll('input[type="text"]');
      otpInputs.forEach((input) => {
        (input as HTMLInputElement).value = '';
      });
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error('Error resending OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };
  
  const handleValidateOTP = async () => {
    setIsVerifying(true);
    setError(""); // Clear any previous errors
    try {
      const cleanCountryCode = countryCode.replace("+", "");
      const response = await authApi.verifyOtp(mobileNumber, otp, cleanCountryCode);
      toast.success(response.message);
      if (response.isExists) {
        dispatch(setUserId(response.data.id.toString()));
        dispatch(setToken(response.accessToken));
        dispatch(setRefreshToken(response.refreshToken));
        dispatch(setFullName(response.data.fullName));
        dispatch(setProfileImage(response.data.profileImage || ''));
        try {
          const configResponse = await authApi.getConfig();
          if (configResponse.data?.googleLocationConfig?.apiKey) {
            dispatch(setGoogleLocationApiKey(configResponse.data.googleLocationConfig.apiKey));
          }
        } catch (configError) {
          console.error('Error fetching config:', configError);
        }
        navigate(`/${country}/my-predictions`);
      } else {
        navigate(`/${country}/enter-name`, {
          state: {
            mobileNumber: mobileNumber,
            countryCode: countryCode
          }
        });
      }
    } catch (error: any) {
      console.error('Error validating OTP:', error);
      const errorMessage = error.response?.data?.message || 'Error validating OTP. Please try again.';
      toast.error(errorMessage);
      setError(errorMessage); // Set the error message in state
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <>
      <Loader isLoading={isVerifying} message={t('verifyingOtp')} />
      <Loader isLoading={isResending} message={t('sendingOtp')} />
      <SuccessPopup 
        handlepopup={showSuccessPopup} 
        message={t('loginSuccessful')} 
        onClose={() => {
          setShowSuccessPopup(false);
          navigate(`/${country}/my-predictions`);
        }}
      />
      <div className="bg-white flex flex-row justify-center w-full h-screen">
        <div className="bg-white w-full h-full relative flex">
          {/* Left side - Background image */}
          <div className="relative flex-1 hidden sm:block">
            <img
              className="w-full h-full object-cover"
              alt="Farm landscape illustration"
              src={webbg}
            />
          </div>

          {/* Right side - OTP verification */}
          <div className="w-full overflow-hidden sm:overflow-auto sm:w-[441px] h-full flex items-start justify-center bg-white p-8">
            <div 
              className="absolute inset-0 bg-no-repeat bg-contain bg-bottom sm:hidden"
              style={{ 
                backgroundImage: `url(${mobilebg})`
              }}
            />
            <Card className="w-full border-none shadow-none relative">
              <CardContent className="p-0">
                {/* Logo */}
                <div className="mb-12">
                  <MainLogo />
                </div>
                <div>
                  {/* Heading */}
                  <h1 className="text-2xl font-semibold text-neutralneutral-90 mb-2">
                    {t('verifyWithOtp')}
                  </h1>

                  {/* Phone number */}
                  <div className="flex items-center justify-between mb-8">
                    <p className="text-sm text-neutralneutral-60">
                      {t('sentViaSms')} {countryCode} {mobileNumber}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-green-700 h-6 px-2 flex items-center gap-1"
                      onClick={handleGoBack}
                    >
                      {t('edit')} <PenLineIcon className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* OTP Input */}
                  <div className="mb-6">
                    <InputOTP
                      value={otp}
                      onChange={(value) => {
                        // Only allow numeric values
                        const numericValue = value.replace(/\D/g, '');
                        setOtp(numericValue);
                        setError(""); // Clear error when user types
                      }}
                      maxLength={6}
                      className={`gap-2 justify-between ${error ? 'border-red-500' : ''}`}
                      pattern="[0-9]*"
                      inputMode="numeric"
                    >
                      <InputOTPGroup className="w-full sm:auto">
                        <InputOTPSlot
                          index={0}
                          className={`h-14 w-full sm:w-14 border-neutralneutral-30 rounded-md ${error ? 'border-red-500' : ''}`}
                          pattern="[0-9]*"
                          inputMode="numeric"
                        />
                      </InputOTPGroup>
                      <InputOTPGroup className="w-full sm:auto">
                        <InputOTPSlot
                          index={1}
                          className={`h-14 w-full sm:w-14 border-neutralneutral-30 rounded-md ${error ? 'border-red-500' : ''}`}
                          pattern="[0-9]*"
                          inputMode="numeric"
                        />
                      </InputOTPGroup>
                      <InputOTPGroup className="w-full sm:auto">
                        <InputOTPSlot
                          index={2}
                          className={`h-14 w-full sm:w-14 border-neutralneutral-30 rounded-md ${error ? 'border-red-500' : ''}`}
                          pattern="[0-9]*"
                          inputMode="numeric"
                        />
                      </InputOTPGroup>
                      <InputOTPGroup className="w-full sm:auto">
                        <InputOTPSlot
                          index={3}
                          className={`h-14 w-full sm:w-14 border-neutralneutral-30 rounded-md ${error ? 'border-red-500' : ''}`}
                          pattern="[0-9]*"
                          inputMode="numeric"
                        />
                      </InputOTPGroup>
                      <InputOTPGroup className="w-full sm:auto">
                        <InputOTPSlot
                          index={4}
                          className={`h-14 w-full sm:w-14 border-neutralneutral-30 rounded-md ${error ? 'border-red-500' : ''}`}
                          pattern="[0-9]*"
                          inputMode="numeric"
                        />
                      </InputOTPGroup>
                      <InputOTPGroup className="w-full sm:auto">
                        <InputOTPSlot
                          index={5}
                          className={`h-14 w-full sm:w-14 border-neutralneutral-30 rounded-md ${error ? 'border-red-500' : ''}`}
                          pattern="[0-9]*"
                          inputMode="numeric"
                        />
                      </InputOTPGroup>
                    </InputOTP>
                    {error && (
                      <p className="text-xs text-red-500 mt-1">
                        {error}
                      </p>
                    )}
                  </div>

                  {/* Validate Button */}
                  <Button
                    className="w-full bg-green-700 hover:bg-green-800 text-white py-6 rounded-md mb-6 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleValidateOTP}
                    disabled={!otp || otp.length < 6}
                  >
                    {t('validateOtp')}
                  </Button>

                  {/* Resend OTP */}
                  <div className="flex flex-row items-center gap-x-2 mb-4 w-full">
                    <p className="text-sm text-neutralneutral-60">{t('resendOtp')}</p>
                    <span className="text-sm text-neutralneutral-90 whitespace-nowrap">{timer}s</span>
                  </div>

                  {/* Retry options */}
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-neutralneutral-60">
                      {t('didntReceive')}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-green-700 text-white hover:bg-green-800 rounded-md h-8 px-3 flex items-center gap-1"
                        disabled={timer > 0}
                        onClick={handleResendOTP}
                      >
                        <MessageSquareIcon className="h-4 w-4" /> {t('sms')}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};